import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { LandingPageContent } from "@shared/schema";

type ViewMode = "anonymous" | "loggedIn";

interface LandingEditContextType {
  isEditMode: boolean;
  setEditMode: (mode: boolean) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  content: Record<string, LandingPageContent>;
  getContent: (contentKey: string, viewMode?: ViewMode) => string;
  updateLocalContent: (contentKey: string, value: string) => void;
  saveAllChanges: () => Promise<void>;
  cancelAllChanges: () => void;
  hasUnsavedChanges: boolean;
  isLoading: boolean;
  isSaving: boolean;
  pageId: string;
}

const LandingEditContext = createContext<LandingEditContextType | null>(null);

// Helper functions for localStorage caching
function getCachedContent(pageId: string): LandingPageContent[] | undefined {
  try {
    const cached = localStorage.getItem(`landing-content-cache-${pageId}`);
    if (cached) return JSON.parse(cached);
  } catch {}
  return undefined;
}

function setCachedContent(pageId: string, data: LandingPageContent[]) {
  try {
    localStorage.setItem(`landing-content-cache-${pageId}`, JSON.stringify(data));
  } catch {}
}

interface LandingEditProviderProps {
  children: ReactNode;
  pageId: string;
  isAdmin: boolean;
  isLoggedIn: boolean;
  defaultContent: Record<string, { anonymous: string; loggedIn: string }>;
}

export function LandingEditProvider({
  children,
  pageId,
  isAdmin,
  isLoggedIn,
  defaultContent,
}: LandingEditProviderProps) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(isLoggedIn ? "loggedIn" : "anonymous");
  const [localEdits, setLocalEdits] = useState<Record<string, string>>({});

  const { data: contentList = [], isLoading } = useQuery<LandingPageContent[]>({
    queryKey: ["/api/landing-content", pageId],
    staleTime: 5 * 60 * 1000, // 5 minutes - content rarely changes
    gcTime: 30 * 60 * 1000, // 30 minutes garbage collection
    placeholderData: () => getCachedContent(pageId),
  });

  const content = contentList.reduce((acc, item) => {
    acc[item.contentKey] = item;
    return acc;
  }, {} as Record<string, LandingPageContent>);

  useEffect(() => {
    if (contentList.length > 0) {
      setCachedContent(pageId, contentList);
    }
  }, [contentList, pageId]);

  const saveMutation = useMutation({
    mutationFn: async (edits: Record<string, string>) => {
      const promises = Object.entries(edits).map(([contentKey, value]) => {
        const existingContent = content[contentKey];
        const payload = {
          pageId,
          contentKey,
          anonymousContent: viewMode === "anonymous" ? value : (existingContent?.anonymousContent || defaultContent[contentKey]?.anonymous || ""),
          loggedInContent: viewMode === "loggedIn" ? value : (existingContent?.loggedInContent || defaultContent[contentKey]?.loggedIn || ""),
          contentType: "text",
          sectionOrder: existingContent?.sectionOrder || 0,
          isVisible: true,
        };
        return apiRequest("POST", "/api/admin/landing-content", payload);
      });
      return Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/landing-content", pageId] });
      setLocalEdits({});
    },
  });

  const getContent = useCallback(
    (contentKey: string, overrideViewMode?: ViewMode) => {
      const mode = overrideViewMode || viewMode;
      
      // Check local edits first (only for current view mode)
      if (localEdits[contentKey] !== undefined) {
        return localEdits[contentKey];
      }
      
      const item = content[contentKey];
      if (item) {
        const value = mode === "anonymous" ? item.anonymousContent : item.loggedInContent;
        if (value) return value;
      }
      const defaults = defaultContent[contentKey];
      if (defaults) {
        return mode === "anonymous" ? defaults.anonymous : defaults.loggedIn;
      }
      return "";
    },
    [content, viewMode, defaultContent, localEdits]
  );

  const updateLocalContent = useCallback((contentKey: string, value: string) => {
    setLocalEdits(prev => ({ ...prev, [contentKey]: value }));
  }, []);

  const saveAllChanges = useCallback(async () => {
    if (Object.keys(localEdits).length === 0) return;
    await saveMutation.mutateAsync(localEdits);
  }, [saveMutation, localEdits]);

  const cancelAllChanges = useCallback(() => {
    setLocalEdits({});
  }, []);

  const setEditMode = useCallback((mode: boolean) => {
    if (!isAdmin) return;
    if (!mode && Object.keys(localEdits).length > 0) {
      // Discard changes when exiting edit mode
      setLocalEdits({});
    }
    setIsEditMode(mode);
  }, [isAdmin, localEdits]);

  const handleSetViewMode = useCallback((mode: ViewMode) => {
    // Clear local edits when switching view modes to prevent confusion
    if (Object.keys(localEdits).length > 0) {
      setLocalEdits({});
    }
    setViewMode(mode);
  }, [localEdits]);

  const hasUnsavedChanges = Object.keys(localEdits).length > 0;

  return (
    <LandingEditContext.Provider
      value={{
        isEditMode,
        setEditMode,
        viewMode,
        setViewMode: handleSetViewMode,
        content,
        getContent,
        updateLocalContent,
        saveAllChanges,
        cancelAllChanges,
        hasUnsavedChanges,
        isLoading,
        isSaving: saveMutation.isPending,
        pageId,
      }}
    >
      {children}
    </LandingEditContext.Provider>
  );
}

export function useLandingEdit() {
  const context = useContext(LandingEditContext);
  if (!context) {
    throw new Error("useLandingEdit must be used within LandingEditProvider");
  }
  return context;
}
